import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ZoomIn, ZoomOut, RefreshCw, Eye, Download } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';

interface Lot {
  id: string | number;
  status: 'disponible' | 'vendido' | 'crédito' | 'vencido';
  lotValue?: number;
  buyerName?: string;
  buyerDPI?: string;
  phoneNumber?: string;
  seller?: string;
  saleDate?: string;
  totalValue?: number;
  amountPaid?: number;
  payments?: any[];
  receiptNumber?: string; // Added for group calculation
}
interface Config {
  filas: number;
  columnas: number;
  nombre?: string;
  ubicacion?: string;
  precioBase?: number;
}

interface LotMapProps {
  lots: Lot[];
  config: Config;
  onLotClick?: (lot: Lot) => void;
}

const STATUS_COLORS: Record<string, string> = {
  'disponible': 'bg-green-500 text-white',
  'vendido': 'bg-red-500 text-white',
  'crédito': 'bg-orange-400 text-white',
};
const STATUS_LABELS: Record<string, string> = {
  'disponible': 'Disponible',
  'vendido': 'Vendido',
  'crédito': 'Crédito',
};

export const LotMap: React.FC<LotMapProps> = ({ lots, config, onLotClick }) => {
  const [zoom, setZoom] = useState(1);
  const [showNumbers, setShowNumbers] = useState(true);
  const [selectedLot, setSelectedLot] = useState<Lot | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const croquisRef = useRef<HTMLDivElement>(null);
  const { expenses } = useAppData();

  // Copiar la función utilitaria de LotTable
  function getAbonosSincronizados(lote: any, expenses: any[]): any[] {
    if (!lote.payments) return [];
    return lote.payments.filter((p: any) => p.type === 'abono' && expenses.some((e: any) => {
      const montoIgual = parseFloat(e.monto) === parseFloat(p.amount);
      const fechaIgual = (e.fecha && p.date) ? (new Date(e.fecha).toISOString().split('T')[0] === new Date(p.date).toISOString().split('T')[0]) : false;
      const tipoIgual = e.tipo === 'abono';
      const cuotaIgual = e.cuotaNumero ? e.cuotaNumero === p.cuotaNumero : true;
      const reciboIgual = (e.referencia && p.receipt) ? e.referencia.includes(p.receipt) : true;
      return montoIgual && fechaIgual && tipoIgual && cuotaIgual && reciboIgual;
    }));
  }

  // Secuencia personalizada de filas (puedes editar este arreglo para cambiar el croquis)
  const croquisRows = [
    { start: 1, end: 18, leftToRight: true },
    { start: 36, end: 19, leftToRight: false },
    { start: 37, end: 56, leftToRight: true },
    { start: 75, end: 57, leftToRight: false },
    { start: 76, end: 93, leftToRight: true },
    { start: 112, end: 94, leftToRight: false },
    { start: 113, end: 131, leftToRight: true },
    { start: 151, end: 132, leftToRight: false },
    { start: 152, end: 170, leftToRight: true },
    { start: 189, end: 171, leftToRight: false },
    { start: 190, end: 208, leftToRight: true },
    { start: 227, end: 209, leftToRight: false },
    { start: 228, end: 245, leftToRight: true },
  ];

  // Genera los números de lote para cada fila según la secuencia
  function getRowNumbers(start: number, end: number, leftToRight: boolean) {
    const arr = [];
    if (leftToRight) {
      for (let i = start; i <= end; i++) arr.push(i);
    } else {
      for (let i = start; i >= end; i--) arr.push(i);
    }
    return arr;
  }

  // Genera la matriz de lotes visuales según la secuencia personalizada
  const lotesVisualesPorFila = croquisRows.map(row => getRowNumbers(row.start, row.end, row.leftToRight));
  const totalLotes = lotesVisualesPorFila.flat().length;

  // NUEVO: función para obtener el lote (individual o de grupo) por número
  function getLotByNumber(num: number) {
    // 1. Buscar documento individual
    let lot = lots.find(l => Number(l.id) === num);
    if (lot) {
      // Verificar si es un lote a crédito totalmente pagado
      if (lot.status === 'crédito') {
        const saldoPendiente = calcularSaldoPendiente(lot);
        if (saldoPendiente <= 0) {
          // Cambiar estado a vendido si está totalmente pagado
          lot = { ...lot, status: 'vendido' as 'vendido' };
        }
      }
      return lot;
    }

    // 2. Buscar si está en algún grupo (idLotes)
    lot = lots.find(l => Array.isArray((l as any).idLotes) && (l as any).idLotes.includes(String(num)));
    if (lot) {
      // Verificar si es un lote a crédito totalmente pagado
      if (lot.status === 'crédito') {
        const saldoPendiente = calcularSaldoPendiente(lot);
        if (saldoPendiente <= 0) {
          // Cambiar estado a vendido si está totalmente pagado
          lot = { ...lot, status: 'vendido' as 'vendido' };
        }
      }
      return lot;
    }

    // 3. Si no existe, retornar como disponible
    return { id: num, status: 'disponible' as 'disponible', lotValue: config.precioBase };
  }

  // Función para calcular el saldo pendiente de un lote (USANDO LA MISMA LÓGICA DEL MODAL)
  function calcularSaldoPendiente(lote: any): number {
    if (!lote || lote.status !== 'crédito') return 0;

    // Buscar el grupo de venta al que pertenece el lote (MISMA LÓGICA DEL MODAL)
    const grupo = lots.filter(l =>
      l.seller === lote.seller &&
      l.buyerName === lote.buyerName &&
      l.buyerDPI === lote.buyerDPI &&
      l.saleDate === lote.saleDate &&
      l.receiptNumber === lote.receiptNumber
    );

    if (grupo.length === 0) return 0;

    // Calcular total del grupo (MISMA LÓGICA DEL MODAL)
    const totalGrupo = grupo.reduce((sum, l) => sum + (l.totalValue || l.lotValue || 0), 0);

    // Calcular enganche grupal (MISMA LÓGICA DEL MODAL)
    let enganches = grupo.flatMap(l => (l.payments || []).filter((p: any) => p.type === 'enganche'));
    let engancheGrupo = 0;
    if (enganches.length > 0) {
      // Si todos los enganches tienen el mismo monto, tomar solo uno
      const allSame = enganches.every(e => Number(e.amount) === Number(enganches[0].amount));
      if (allSame) {
        engancheGrupo = Number(enganches[0].amount);
      } else {
        // Si hay varios montos, sumar solo los distintos
        engancheGrupo = Array.from(new Set(enganches.map(e => Number(e.amount)))).reduce((a, b) => a + b, 0);
      }
    }

    // Calcular abonos grupales (MISMA LÓGICA DEL MODAL)
    const abonosGrupo = grupo.reduce((sum, l) =>
      sum + (l.payments || []).filter((p: any) => p.type === 'abono')
        .reduce((s: number, p: any) => s + Number(p.amount), 0), 0);

    // Calcular saldo pendiente (MISMA LÓGICA DEL MODAL)
    const totalPagadoGrupo = engancheGrupo + abonosGrupo;
    const saldoPendienteGrupo = totalGrupo - totalPagadoGrupo;

    return Math.max(saldoPendienteGrupo, 0);
  }

  const lotesVisuales = lotesVisualesPorFila.flat().map((num) => getLotByNumber(num));

  // Estadísticas usando la secuencia personalizada
  const stats = lotesVisuales.reduce((acc, lot) => {
    if (!lot || lot.status === 'disponible') acc.disponible++;
    else if (lot.status === 'vendido') acc.vendido++;
    else if (lot.status === 'crédito') acc.credito++;
    return acc;
  }, { disponible: 0, vendido: 0, credito: 0 });

  // Descargar imagen del croquis (incluye leyenda y fecha)
  const handleDownload = async () => {
    if (!croquisRef.current) return;
    // Mostrar temporalmente la leyenda y fecha si están ocultas
    const leyenda = croquisRef.current.querySelector('.leyenda-croquis') as HTMLElement;
    const fecha = croquisRef.current.querySelector('.fecha-croquis') as HTMLElement;
    if (leyenda) leyenda.style.display = 'flex';
    if (fecha) fecha.style.display = 'block';
    await new Promise(r => setTimeout(r, 100)); // Esperar a que se renderice
    const canvas = await html2canvas(croquisRef.current, { scale: 2 });
    if (leyenda) leyenda.style.display = '';
    if (fecha) fecha.style.display = '';
    const link = document.createElement('a');
    const fechaStr = new Date().toISOString().split('T')[0];
    link.download = `${config.nombre || 'croquis'}_${fechaStr}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Modal de detalle
  const openModal = (lot: Lot) => {
    setSelectedLot(lot);
    setModalOpen(true);
    if (onLotClick) onLotClick(lot);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          size="sm"
          variant="outline"
          onClick={handleDownload}
          className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" /> Descargar Imagen
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setZoom(z => Math.min(z + 0.1, 2))}
          className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))}
          className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setZoom(1)}
          className="bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant={showNumbers ? 'default' : 'outline'}
          onClick={() => setShowNumbers(s => !s)}
          className={showNumbers
            ? 'bg-orange-500 text-white hover:bg-orange-600 rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3'
            : 'bg-transparent border border-border hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3'
          }
        >
          <Eye className="h-3.5 w-3.5 mr-1.5" /> {showNumbers ? 'Ocultar Números' : 'Mostrar Números'}
        </Button>
      </div>
      <div className="w-full overflow-x-auto">
        <div ref={croquisRef} className="mx-auto p-4 bg-card rounded-xl shadow-inner border border-border" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: 'fit-content', minWidth: 300 }}>
          {/* Leyenda de estados dentro del área descargable */}
          <div className="leyenda-croquis flex flex-wrap gap-4 mb-4" style={{ display: 'flex' }}>
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded"><span className="w-3 h-3 rounded bg-emerald-500 inline-block"></span> <span className="text-foreground">Disponible</span> <span className="ml-1 font-bold text-foreground">{stats.disponible}</span></div>
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-3 py-1 rounded"><span className="w-3 h-3 rounded bg-red-500 inline-block"></span> <span className="text-foreground">Vendido</span> <span className="ml-1 font-bold text-foreground">{stats.vendido}</span></div>
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-3 py-1 rounded"><span className="w-3 h-3 rounded bg-orange-400 inline-block"></span> <span className="text-foreground">Crédito</span> <span className="ml-1 font-bold text-foreground">{stats.credito}</span></div>
          </div>
          <div className="flex flex-col gap-2">
            {[...lotesVisualesPorFila].reverse().map((fila, filaIdx) => (
              <div key={filaIdx} className="flex gap-2">
                {fila.map((num, idx) => {
                  const lot = getLotByNumber(num);
                  const status = lot.status;
                  return (
                    <div key={num} className="relative group">
                      <button
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-lg font-bold text-xs md:text-sm flex items-center justify-center border border-border shadow-sm transition-all duration-150 cursor-pointer hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-400 ${STATUS_COLORS[status]}`}
                        onClick={() => openModal(lot)}
                      >
                        {showNumbers && num}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Fecha de actualización dentro del área descargable */}
            <div className="fecha-croquis text-xs text-muted-foreground mt-4 text-right" style={{ display: 'block' }}>Actualizado: {new Date().toLocaleString()}</div>
          </div>
        </div>
      </div>
      {/* Modal de detalle con tema Linear */}
      {modalOpen && selectedLot && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl shadow-2xl p-6 w-full max-w-2xl relative border border-border">
            <button className="absolute top-4 right-4 text-muted-foreground hover:text-destructive text-2xl font-bold" onClick={() => setModalOpen(false)}>&times;</button>
            <div className="mb-2 flex items-center gap-3">
              <span className="text-2xl">🏷️</span>
              <h2 className="text-2xl font-bold text-foreground">Información del Lote #{selectedLot.id}</h2>
            </div>
            <div className="text-muted-foreground text-sm mb-4">Detalles completos y estado actual del lote</div>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-3 h-3 rounded-full bg-orange-400"></span>
              <span className="font-semibold text-foreground">Estado del Lote</span>
              <span className={`ml-auto px-3 py-1 rounded-lg text-sm font-semibold border ${selectedLot.status === 'crédito' ? 'bg-orange-500/10 text-orange-600 border-orange-500/20' : selectedLot.status === 'vendido' ? 'bg-red-500/10 text-red-600 border-red-500/20' : 'bg-green-500/10 text-green-600 border-green-500/20'}`}>{STATUS_LABELS[selectedLot.status]}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-secondary/50 border border-border rounded-xl p-4">
                <div className="font-semibold text-foreground mb-2 flex items-center gap-2"><span className="text-lg">📍</span> Información del Lote</div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Número de Lote:</span> <span className="font-mono text-lg text-foreground ml-1">#{selectedLot.id}</span></div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Precio:</span> <span className="text-green-600 font-bold ml-1">Q {selectedLot.lotValue?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Estado:</span> <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ml-1 ${selectedLot.status === 'crédito' ? 'bg-orange-500/10 text-orange-600 border border-orange-500/20' : selectedLot.status === 'vendido' ? 'bg-red-500/10 text-red-600 border border-red-500/20' : 'bg-green-500/10 text-green-600 border-green-500/20'}`}>{STATUS_LABELS[selectedLot.status]}</span></div>
              </div>
              <div className="bg-secondary/50 border border-border rounded-xl p-4">
                <div className="font-semibold text-foreground mb-2 flex items-center gap-2"><span className="text-lg">👤</span> Información del Comprador</div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Nombre:</span> <span className="font-bold text-foreground ml-1">{selectedLot.buyerName || '-'}</span></div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">DPI:</span> <span className="text-foreground ml-1">{selectedLot.buyerDPI || '-'}</span></div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Teléfono:</span> <span className="text-foreground ml-1">{selectedLot.phoneNumber || '-'}</span></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-secondary/50 border border-border rounded-xl p-4">
                <div className="font-semibold text-foreground mb-2 flex items-center gap-2"><span className="text-lg">📝</span> Información de Venta</div>
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Vendedor:</span> <span className="font-bold text-foreground ml-1">{selectedLot.seller || '-'}</span></div>
                {/* Fecha de venta grupal precisa */}
                {(() => {
                  const grupo = lots.filter(l =>
                    l.seller === selectedLot.seller &&
                    l.buyerName === selectedLot.buyerName &&
                    l.buyerDPI === selectedLot.buyerDPI &&
                    l.receiptNumber === selectedLot.receiptNumber
                  );
                  // Tomar la fecha de venta más común o la primera del grupo
                  const fechas = grupo.map(l => l.saleDate).filter((f): f is string => !!f);
                  let fechaVenta = fechas[0] || selectedLot.saleDate || '-';
                  if (fechas.length > 1) {
                    // Buscar la fecha más frecuente
                    const freq: Record<string, number> = {};
                    fechas.forEach(f => { freq[f] = (freq[f] || 0) + 1; });
                    fechaVenta = Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0];
                  }
                  return <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Fecha de Venta:</span> <span className="text-foreground ml-1">{fechaVenta}</span></div>;
                })()}
                <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Fecha de Registro:</span> <span className="text-foreground ml-1">{selectedLot.saleDate || '-'}</span></div>
              </div>
              <div className="bg-secondary/50 border border-border rounded-xl p-4">
                <div className="font-semibold text-foreground mb-2 flex items-center gap-2"><span className="text-lg">💲</span> Estado Financiero</div>
                {(() => {
                  // Buscar el grupo de venta al que pertenece el lote
                  const grupo = lots.filter(l =>
                    l.seller === selectedLot.seller &&
                    l.buyerName === selectedLot.buyerName &&
                    l.buyerDPI === selectedLot.buyerDPI &&
                    l.saleDate === selectedLot.saleDate &&
                    l.receiptNumber === selectedLot.receiptNumber
                  );
                  const totalGrupo = grupo.reduce((sum, l) => sum + (l.totalValue || l.lotValue || 0), 0);
                  // Enganche grupal: tomar solo el primer enganche del grupo (o sumar todos y dividir entre la cantidad de lotes si están duplicados)
                  let enganches = grupo.flatMap(l => (l.payments || []).filter((p: any) => p.type === 'enganche'));
                  let engancheGrupo = 0;
                  if (enganches.length > 0) {
                    // Si todos los enganches tienen el mismo monto, tomar solo uno
                    const allSame = enganches.every(e => Number(e.amount) === Number(enganches[0].amount));
                    if (allSame) {
                      engancheGrupo = Number(enganches[0].amount);
                    } else {
                      // Si hay varios montos, sumar solo los distintos (caso raro)
                      engancheGrupo = Array.from(new Set(enganches.map(e => Number(e.amount)))).reduce((a, b) => a + b, 0);
                    }
                  }
                  // Sumar todos los abonos grupales
                  const abonosGrupo = grupo.reduce((sum, l) => sum + (l.payments || []).filter((p: any) => p.type === 'abono').reduce((s: number, p: any) => s + Number(p.amount), 0), 0);
                  const totalPagadoGrupo = engancheGrupo + abonosGrupo;
                  const saldoPendienteGrupo = totalGrupo - totalPagadoGrupo;
                  return <>
                    <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Total de la venta grupal:</span> <span className="text-green-600 font-bold ml-1">Q {totalGrupo.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                    <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Total pagado (grupo):</span> <span className="text-green-600 font-bold ml-1">Q {totalPagadoGrupo.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                    <div className="mb-1 text-sm"><span className="font-semibold text-muted-foreground">Saldo pendiente (grupo):</span> <span className="text-orange-600 font-bold ml-1">Q {saldoPendienteGrupo.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                  </>;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};